/**
 * RoleComparisonMatrixDialog - Role permissions comparison matrix dialog
 * Extracted from SettingsPage to reduce file size
 */
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Users } from "lucide-react";

export function RoleComparisonMatrixDialog({
  open,
  onOpenChange,
  permissions,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Role Permissions Comparison Matrix
          </DialogTitle>
          <DialogDescription>
            Side-by-side comparison of all role permissions
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-auto flex-1 -mx-6 px-6">
          {permissions && (
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-2 font-semibold text-slate-700 bg-slate-50 min-w-[180px]">Permission</th>
                  <th className="text-center py-3 px-2 min-w-[80px]">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">Admin</span>
                  </th>
                  <th className="text-center py-3 px-2 min-w-[80px]">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Case Mgr</span>
                  </th>
                  <th className="text-center py-3 px-2 min-w-[80px]">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-700">HR/Sched</span>
                  </th>
                  <th className="text-center py-3 px-2 min-w-[80px]">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700">EVV Spec</span>
                  </th>
                  <th className="text-center py-3 px-2 min-w-[80px]">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Intake</span>
                  </th>
                  <th className="text-center py-3 px-2 min-w-[80px]">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-700">Reception</span>
                  </th>
                  <th className="text-center py-3 px-2 min-w-[80px]">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">RN</span>
                  </th>
                  <th className="text-center py-3 px-2 min-w-[80px]">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">PCW</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {permissions.permissions?.admin && Object.entries(permissions.permissions.admin).map(([feature, perms]) => {
                  const isSimple = typeof perms === "boolean";
                  const featureLabel = feature.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
                  
                  if (isSimple) {
                    return (
                      <tr key={feature} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-2 font-medium text-slate-700">{featureLabel}</td>
                        {["admin", "case_manager", "hr_scheduler", "evv_specialist", "intake_coordinator", "receptionist", "rn", "pcw"].map((role) => {
                          const val = permissions.permissions[role]?.[feature];
                          return (
                            <td key={role} className="text-center py-2 px-2">
                              {val ? (
                                <span className="text-emerald-600">✓</span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  } else {
                    return (
                      <React.Fragment key={feature}>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <td colSpan={9} className="py-2 px-2 font-semibold text-slate-800">{featureLabel}</td>
                        </tr>
                        {Object.keys(perms).map((action) => (
                          <tr key={`${feature}-${action}`} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2 px-2 pl-6 text-slate-600 capitalize">{action}</td>
                            {["admin", "case_manager", "hr_scheduler", "evv_specialist", "intake_coordinator", "receptionist", "rn", "pcw"].map((role) => {
                              const val = permissions.permissions[role]?.[feature]?.[action];
                              return (
                                <td key={role} className="text-center py-2 px-2">
                                  {val ? (
                                    <span className="text-emerald-600 font-bold">✓</span>
                                  ) : (
                                    <span className="text-red-300">✗</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  }
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="pt-4 border-t flex items-center justify-between">
          <div className="flex gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="text-emerald-600 font-bold">✓</span> Allowed</span>
            <span className="flex items-center gap-1"><span className="text-red-300">✗</span> Denied</span>
            <span className="flex items-center gap-1"><span className="text-slate-300">—</span> N/A</span>
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
